import { FC, useEffect, useState } from "react";
import { Form, Input, InputNumber, Modal, notification, Select, TreeSelect, Radio, Flex, Button, Spin } from "antd";
import { Product, ProductCategory, ProductUpdate } from "@/types/product";
import { categoryFetch } from "@/apis/product";
import useCategories from "@/pages/ProductCategory/hooks/useCategories";
import { useTranslation } from 'react-i18next';
import useProductUpdate from "../hooks/useProductUpdate";
import { Rule } from "antd/es/form";
import useProductAdd from "../hooks/useProductAdd";
import { UpperCaseInput } from "../style";
import ProductUomSelect from "@/components/Selector/ProductUomSelect";
import { useSelector } from "react-redux";
import { RootState } from "@/store/index";
import { User, UserPermissionEnum } from "@/types/user";
import { hasPermission } from "@/utils/user";
import ProductSelect from "@/components/Selector/ProductSelect";

interface IAddOrEdit {
  isAdd?: boolean;
  openStatus: boolean;
  formData: Product | null;
  categoryId?: string | null;
  onOk: (product: Product) => void;
  onCancel: () => void;
}
interface TreeSelectData extends ProductCategory {
  disabled?: boolean
}

type FormFail = {
  errorFields?: Array<{ name: string; errors: string[] }>;
  values?: Product;
  outOfDate?: boolean;
}

const findCategoryById = (nodes: Array<ProductCategory> | undefined, id: string): ProductCategory | null => {
  if (!nodes || nodes.length === 0) return null;
  for (const node of nodes) {
    if (node.id === id) return node;
    const inChild = findCategoryById(node.subs, id);
    if (inChild) return inChild;
  }
  return null;
};

const AddOrEdit: FC<IAddOrEdit> = ({ isAdd, openStatus, formData, categoryId, onOk, onCancel, }) => {
  const { t } = useTranslation();
  const [inForm] = Form.useForm();
  const curUser: User = useSelector((state: RootState) => state.userReducer.curUser);
  const cateOrgPermission = hasPermission(curUser, [UserPermissionEnum.PRODUCT_CATEGORY_ORG_SET]);
  const [categorieOption, , caLoading] = useCategories({ keyword: "", cateOrgPermission });
  const [loading, { update }] = useProductUpdate();
  const [addLoading, { add }] = useProductAdd();
  const [category, setCategory] = useState<ProductCategory | null>(formData?.category ?? null);
  const [categoryTreeData, setCategoryTreeData] = useState<Array<ProductCategory>>([]);

  useEffect(() => {
    setCategoryTreeData((prev) => {
      const nextBase = categorieOption ?? [];
      if (!prev || prev.length === 0) return nextBase;
      const extras = prev.filter((n) => !findCategoryById(nextBase, n.id));
      return extras.length > 0 ? [...extras, ...nextBase] : nextBase;
    });
  }, [categorieOption]);

  useEffect(() => {
    const selectedId = categoryId ?? formData?.categoryId;
    if (!selectedId) return;
    if (findCategoryById(categoryTreeData, selectedId)) return;

    categoryFetch(selectedId)
      .then((cat) => {
        setCategory(cat);
        setCategoryTreeData((prev) => {
          if (findCategoryById(prev, selectedId)) return prev;
          return [cat, ...prev];
        });
      })
      .catch(() => {
        // ignore: keep showing raw id if backend refuses
      });
  }, [categoryId, formData?.categoryId, categoryTreeData]);

  useEffect(() => {
    if (formData) {
      inForm.setFieldsValue({
        ...formData,
        categoryId: formData.categoryId,
        process: formData?.process ? formData.process.split(',') : []
      })
    }
  }, [formData, inForm])

  useEffect(() => {
    if (categoryId) {
      inForm.setFieldValue('categoryId', categoryId)
    }
  }, [categoryId, isAdd, inForm])

  const cbmChange = (newValue: number | null) => {
    if (newValue) {
      const newValueStr = newValue.toString();
      const decimalPart = newValueStr.split('.')[1];
      if (decimalPart && decimalPart.length > 10) {
        notification.warning({ placement: 'top', message: t('product.cbmDecimalLength', { count: 10 }) });
        return;
      }
    }
  }
  // 商品类目新增只允许选择二/三级类目
  const treeNodeDisable = (nodes: TreeSelectData[]) => {
    return nodes?.map(node => {
      const newNode = { ...node };
      if (newNode.path.split('#').length < 2) {
        newNode.disabled = true;
      }
      if (newNode.subs && newNode.subs.length > 0) {
        newNode.subs = treeNodeDisable(newNode.subs);
      }
      return newNode;
    });
  }

  const onFinish = (addNext?: boolean) => {
    if (isAdd) {
      inForm.validateFields().then((values) => {
        let process = values.process
        if (process && process.length > 0) process = process.join(',')
        else process = undefined
        add({
          ...values,
          process,
        }).then((res) => {
          const pro = { ...res, process }
          category && (pro.category = category)
          onOk(pro);
          if (addNext) inForm.resetFields()
          else handleCancel();
        });
      }).catch((fail: FormFail) => requireTip(fail));
    } else {
      if (formData && formData.id) {
        inForm.validateFields().then((values) => {
          let process = values.process
          if (process && process.length > 0) process = process.join(',')
          else process = undefined
          update(formData?.id, {
            ...values,
            process
          }).then(() => {
            onOk({ id: formData.id, ...values, category, process });
            handleCancel();
          });
        }).catch((fail: FormFail) => requireTip(fail));
      }
    }
  };

  const requireTip = (fail: FormFail) => {
    const errMsg = fail?.errorFields?.map(item => item?.errors).join(',');
    notification.warning({ placement: 'top', message: errMsg || t('product.plFill') })
  }

  const handleCancel = () => {
    inForm.resetFields();
    onCancel();
  };

  const getProductFormItem = (
    label: string,
    key: keyof ProductUpdate,
    placeholder: string,
    rules?: Rule[],
    count?: { show: boolean, max: number },
    disabled?: boolean,
  ) => {
    return (
      <Form.Item<ProductUpdate> label={label} name={key} rules={rules} >
        <Input
          placeholder={placeholder}
          value={key}
          count={count}
          disabled={disabled}
          min={key === 'code' ? 0 : undefined}
          onChange={(e) => key === 'code' && inForm.setFieldsValue({ code: e.target.value.replace(/\s/g, '') })}
        />
      </Form.Item>
    );
  };

  return (
    <Modal
      title={isAdd ? t('category.addProduct') : t('product.modifyProduct')}
      open={openStatus}
      confirmLoading={loading}
      onCancel={handleCancel}
      footer={
        <Flex justify='flex-end' gap={8}>
          <Button disabled={addLoading || loading} onClick={handleCancel}>{t('common.cancel')}</Button>
          <Button disabled={addLoading || loading} type='dashed' onClick={() => onFinish(true)}>{t('common.addNext')}</Button>
          <Button disabled={addLoading || loading} type='primary' onClick={() => onFinish()}>{t('common.confirm')}</Button>
        </Flex>
      }
      maskClosable={false}
      destroyOnClose
    >
      <Spin spinning={addLoading || loading}>
        <Form form={inForm} name="product" autoComplete="off" labelAlign="left">
          {getProductFormItem(t('product.code'), 'code', t('product.plCode'),
            [{ required: true, message: t('product.plCode') }, { min: 4, max: 24, message: t('product.plCodeRule', { min: 4, max: 24 }) }],
            { show: true, max: 24, },
            !isAdd && true)
          }
          {
            getProductFormItem(t('product.name'), 'name', t('product.plName'),
              [{ required: true, message: t('product.plName') }, { max: 100, message: t('product.plNameRule', { length: 100 }) }],
              { show: true, max: 100 },
            )
          }
          <Form.Item<ProductUpdate> label={t('common.desc')} name="desc" rules={[{ max: 1000, message: t('member.maxLengthTip') }]}>
            <Input.TextArea autoSize={{ minRows: 4, maxRows: 8 }} count={{ show: true, max: 1000 }} placeholder={t('product.plDesc')} />
          </Form.Item>
          <Form.Item<ProductUpdate>
            label={t('product.brand1')}
            name={'model'}
            rules={[{ max: 100, message: t('member.maxLengthTip') }]}
          >
            <UpperCaseInput placeholder={t('product.plBrand1')} count={{ show: true, max: 100 }} />
          </Form.Item>
          <Form.Item<ProductUpdate>
            label={t('product.brand2')}
            name={'t1'}
            rules={[{ max: 100, message: t('member.maxLengthTip') }]}
          >
            <UpperCaseInput placeholder={t('product.plBrand2')} count={{ show: true, max: 100 }} />
          </Form.Item>
          <Form.Item<ProductUpdate>
            label={t('product.uom')}
            name={'uom'}
          >
            <ProductUomSelect />
          </Form.Item>
          <Form.Item<ProductUpdate> label={`cbm(${t('product.cbm')})`} name="cbm">
            <InputNumber changeOnWheel
              placeholder={t('product.plCbm')}
              min={0}
              precision={10}
              onChange={cbmChange}
              style={{ width: '100%' }}
              stringMode
            />
          </Form.Item>
          <Form.Item<ProductUpdate> label={t('product.process')} name="process" >
            <Select mode="multiple"
              placeholder={t('product.processPl')}
              options={[
                { value: 'SMT', label: 'SMT' },
                { value: 'DIP', label: 'DIP' },
                { value: 'ASSY', label: 'ASSY' },
                { value: 'PACK', label: 'PACK' },
                { value: 'TEST', label: 'TEST' },
                { value: 'SMT + DIP', label: 'SMT + DIP' },
                { value: 'SMT + DIP + TEST', label: 'SMT + DIP + TEST' },
                { value: 'SMT + DIP + PACK', label: 'SMT + DIP + PACK' },
                { value: 'SMT + DIP + TEST + ASSY', label: 'SMT + DIP + TEST + ASSY' },
              ]}
            />
          </Form.Item>
          <Form.Item<ProductUpdate> label={t('category.hsCode')} name="hsCode">
            <Input count={{ show: true, max: 20 }} placeholder={t('category.plHsCode')} />
          </Form.Item>
          <Form.Item<ProductUpdate> label={t('product.category')} name="categoryId">
            <TreeSelect
              placeholder={t('category.selCategory')}
              showSearch
              treeNodeFilterProp="name"
              loading={caLoading}
              treeData={treeNodeDisable(categoryTreeData)}
              fieldNames={{ label: 'name', value: 'id', children: 'subs' }}
              treeDefaultExpandAll
              onSelect={(value, val) => {
                inForm.setFieldValue('categoryId', value)
                setCategory(val)
              }}
              filterTreeNode={(inputVal: string, node: any) => {
                return node.name?.toLowerCase().includes(inputVal.toLowerCase()) ||
                  node.code?.toLowerCase().includes(inputVal.toLowerCase())
              }}
            />
          </Form.Item>
          <Form.Item label={t('product.preReady')} name="preReady" initialValue={0} >
            <Radio.Group
              options={[
                { label: t('product.isTrue'), value: 1 },
                { label: t('product.isFalse'), value: 0 },
              ]}
            />
          </Form.Item>
          <Form.Item label={t('product.isDeprecated')} name="deprecated" initialValue={null} >
            <Radio.Group
              options={[
                { label: t('product.deprecated'), value: 1 },
                { label: t('product.normalStatus'), value: 0 },
                { label: t('product.notSet'), value: null },
              ]}
            />
          </Form.Item>
          <Form.Item label={t('bom.itemReq')} name="replacementId" >
            <ProductSelect disabledProducts={formData ? [formData.id] : undefined} defaultList={formData?.replacement ? [formData.replacement] : undefined} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal >
  );
};

export default AddOrEdit;